import * as THREE from 'three';
import type { PlacementDocument } from '../course/CourseDocument';
import type { BallState } from './BallState';

export interface ObstacleColliderDef {
  id: string;
  kind: 'tree' | 'rock';
  position: THREE.Vector3;
  radius: number;
  height: number;
  enabled: boolean;
}

export class ObstacleSpatialIndex {
  private readonly cells = new Map<string, ObstacleColliderDef[]>();
  constructor(private readonly cellSize = 18) {}
  insert(collider: ObstacleColliderDef): void {
    const { x, z } = collider.position;
    const r = collider.radius;
    for (
      let ix = Math.floor((x - r) / this.cellSize);
      ix <= Math.floor((x + r) / this.cellSize);
      ix++
    )
      for (
        let iz = Math.floor((z - r) / this.cellSize);
        iz <= Math.floor((z + r) / this.cellSize);
        iz++
      ) {
        const key = `${ix}:${iz}`;
        const bucket = this.cells.get(key) ?? [];
        bucket.push(collider);
        this.cells.set(key, bucket);
      }
  }
  querySegment(a: THREE.Vector3, b: THREE.Vector3, radius: number): ObstacleColliderDef[] {
    const minX = Math.min(a.x, b.x) - radius,
      maxX = Math.max(a.x, b.x) + radius;
    const minZ = Math.min(a.z, b.z) - radius,
      maxZ = Math.max(a.z, b.z) + radius;
    const found = new Map<string, ObstacleColliderDef>();
    for (let ix = Math.floor(minX / this.cellSize); ix <= Math.floor(maxX / this.cellSize); ix++)
      for (let iz = Math.floor(minZ / this.cellSize); iz <= Math.floor(maxZ / this.cellSize); iz++)
        for (const c of this.cells.get(`${ix}:${iz}`) ?? []) found.set(c.id, c);
    return [...found.values()].filter((c) => c.enabled);
  }
}

export class ObstacleCollisionResolver {
  constructor(private readonly index: ObstacleSpatialIndex) {}
  resolve(state: BallState, previousPosition: THREE.Vector3): boolean {
    for (const collider of this.index.querySegment(previousPosition, state.position, 8)) {
      const hit = closestPointOnSegment2(previousPosition, state.position, collider.position);
      const dist = Math.hypot(hit.x - collider.position.x, hit.z - collider.position.z);
      if (dist > collider.radius || state.position.y > collider.position.y + collider.height)
        continue;
      const normal = new THREE.Vector3(
        state.position.x - collider.position.x,
        0,
        state.position.z - collider.position.z,
      );
      if (normal.lengthSq() < 0.001) normal.set(1, 0, 0);
      normal.normalize();
      const restitution = collider.kind === 'rock' ? 0.72 : 0.28;
      state.velocity.reflect(normal).multiplyScalar(restitution);
      if (collider.kind === 'tree') state.velocity.y = Math.min(state.velocity.y, -0.8);
      state.position
        .copy(collider.position)
        .addScaledVector(normal, collider.radius + 0.05)
        .setY(state.position.y);
      return true;
    }
    return false;
  }
}

export function collidersFromPlacements(
  placements: readonly PlacementDocument[],
): ObstacleColliderDef[] {
  return placements.map((p) => ({
    id: p.id,
    kind: p.kind,
    position: new THREE.Vector3(p.position.x, p.position.y ?? 0, p.position.z),
    radius: p.kind === 'tree' ? 1.1 * (p.scale ?? 1) : (p.radius ?? 2) * (p.scale ?? 1),
    height: p.kind === 'tree' ? 8 * (p.scale ?? 1) : 2.5 * (p.scale ?? 1),
    enabled: true,
  }));
}

function closestPointOnSegment2(
  a: THREE.Vector3,
  b: THREE.Vector3,
  p: THREE.Vector3,
): THREE.Vector3 {
  const dx = b.x - a.x,
    dz = b.z - a.z;
  const lenSq = dx * dx + dz * dz || 1;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq));
  return new THREE.Vector3(a.x + t * dx, 0, a.z + t * dz);
}
